import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    getThemes,
    getThemeById,
    applyTheme,
    getStoredTheme,
    getDefaultTheme
} from '../services/themesService';
import { getSelectedThemeId, saveSelectedThemeId } from '../services/preferencesService';

const ThemeContext = createContext();

// Default theme (Cabaza Purple) - fallback when no theme is loaded
const DEFAULT_THEME = {
    id: 1,
    name: 'Cabaza Purple',
    nameFr: 'Violet Cabaza',
    nameEn: 'Cabaza Purple',
    themeType: 'gradient',
    primaryColor: '#8b5cf6',
    secondaryColor: '#ec4899',
    accentColor: '#8b5cf6',
    backgroundColor: '#ffffff',
    surfaceColor: '#f9fafb',
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textOnPrimary: '#ffffff',
    borderColor: '#e5e7eb',
    isSystem: true,
    isDefault: true
};

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState(() => {
        // Try to get stored theme first
        const stored = getStoredTheme();
        return stored || DEFAULT_THEME;
    });
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load themes and apply stored theme
    useEffect(() => {
        const initializeTheme = async () => {
            try {
                // Load all available themes from DB
                const allThemes = await getThemes();
                setThemes(allThemes);

                // Priority: 1. localStorage (fast), 2. API preferences (synced), 3. default
                const stored = getStoredTheme();
                if (stored) {
                    // Use localStorage for fast initial load
                    applyTheme(stored);
                }

                // Check API preferences for synced theme (only if logged in)
                const userId = localStorage.getItem('userId');
                if (userId) {
                    const savedThemeId = await getSelectedThemeId();
                    if (savedThemeId && savedThemeId > 0) {
                        // DB has a theme - load it to localStorage
                        let syncedTheme = allThemes.find(t => t.id === savedThemeId);
                        if (!syncedTheme) {
                            syncedTheme = await getThemeById(savedThemeId);
                        }
                        if (syncedTheme && (!stored || stored.id !== savedThemeId)) {
                            console.log('Syncing theme from API:', syncedTheme.name);
                            setCurrentTheme(syncedTheme);
                            applyTheme(syncedTheme);
                        }
                    } else if (stored && stored.id) {
                        // DB is empty but localStorage has a theme - push to API
                        console.log('Pushing theme from localStorage to API:', stored.name);
                        saveSelectedThemeId(stored.id);
                    }
                }

                // Fallback to default if nothing stored
                if (!stored && !localStorage.getItem('userId')) {
                    const defaultTheme = await getDefaultTheme();
                    if (defaultTheme) {
                        setCurrentTheme(defaultTheme);
                        applyTheme(defaultTheme);
                    } else {
                        applyTheme(DEFAULT_THEME);
                    }
                }
            } catch (error) {
                console.error('Error initializing theme:', error);
                applyTheme(DEFAULT_THEME);
            } finally {
                setLoading(false);
            }
        };

        initializeTheme();
    }, []);

    // Change theme (stores in localStorage via applyTheme and syncs to API)
    const changeTheme = useCallback((theme) => {
        setCurrentTheme(theme);
        applyTheme(theme); // This saves to localStorage

        // Sync to API if logged in
        if (localStorage.getItem('userId') && theme.id) {
            saveSelectedThemeId(theme.id);
        }
    }, []);

    // Apply custom theme (from color picker)
    const applyCustomTheme = useCallback((customColors) => {
        const customTheme = {
            ...DEFAULT_THEME,
            ...customColors,
            id: 0,
            name: 'Custom',
            nameFr: 'Personnalise',
            nameEn: 'Custom',
            themeType: 'custom',
            isSystem: false,
            isDefault: false
        };

        setCurrentTheme(customTheme);
        applyTheme(customTheme); // This saves to localStorage
    }, []);

    // Reset to default theme
    const resetToDefault = useCallback(() => {
        const defaultTheme = themes.find(t => t.isDefault) || DEFAULT_THEME;
        changeTheme(defaultTheme);
    }, [themes, changeTheme]);

    // Refresh themes list
    const refreshThemes = useCallback(async () => {
        const allThemes = await getThemes();
        setThemes(allThemes);
    }, []);

    // Check if current theme is dark
    const isDarkTheme = currentTheme?.themeType === 'flat-dark';

    const value = {
        currentTheme,
        themes,
        loading,
        isDarkTheme,
        changeTheme,
        applyCustomTheme,
        resetToDefault,
        refreshThemes,
        DEFAULT_THEME
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
